import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
  // ** jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // ** For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // ** For Refreshing Token
  subscribers = []

  // ** Flag to track if refresh token has failed
  refreshTokenFailed = false

  // ** Flag to track if logout is in progress
  isLoggingOut = false

  // ✅ FIX #7: Add refresh cooldown mechanism
  // ** Track last refresh time to prevent excessive refresh attempts
  lastRefreshTime = 0
  refreshCooldown = 60000 // 1 minute cooldown between refresh attempts

  // ** Store axios instance
  axiosIns = null

  constructor(jwtOverrideConfig, axiosInstance) {
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // ✅ CRITICAL FIX: Use the provided axios instance instead of importing base axios
    // This ensures interceptors are added to the same instance used by the application
    this.axiosIns = axiosInstance

    if (!this.axiosIns) {
      console.error('❌ JwtService: No axios instance provided! Interceptors will not work!');
      return;
    }

    console.log('✅ JwtService: Initializing with provided axios instance');

    // ** Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        // ** Get token from localStorage
        const accessToken = this.getToken()

        // ** If token is present add it to request's Authorization Header
        if (accessToken) {
          // ** eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        } else {
          // ** If no token, remove Authorization header to prevent using stale token
          // ** This is important when tokens are manually removed from localStorage
          delete config.headers.Authorization
        }
        return config
      },
      error => Promise.reject(error)
    )

    // ** Add request/response interceptor
    this.axiosIns.interceptors.response.use(
     response => response,
     error => {
     // ** const { config, response: { status } } = error
     const { config, response } = error
     const originalRequest = config

     // If logout is in progress, reject all requests immediately
     if (this.isLoggingOut) {
     console.log('JwtService interceptor: Logout in progress, rejecting request');
     // Mark error as handled to prevent showing error toasts
     error._handledByTokenExpiration = true;
     return Promise.reject(error);
     }

     // If refresh token has already failed, don't try to refresh again
     if (this.refreshTokenFailed) {
     console.log('JwtService interceptor: Refresh token has already failed, not attempting refresh');
     // Mark error as handled to prevent showing error toasts
     error._handledByTokenExpiration = true;
     return Promise.reject(error);
     }

     // ** if (status === 401) {
     if (response && response.status === 401) {
     console.log(`JwtService interceptor: Received 401 error from ${originalRequest?.url}`);

     // ✅ CRITICAL FIX: Mark error as handled IMMEDIATELY to prevent duplicate toasts
     // This ensures all error handlers skip this error while we handle token refresh
     error._handledByTokenExpiration = true;

     // ✅ Check if we have a refresh token before attempting refresh
     const currentRefreshToken = this.getRefreshToken();
     const currentAccessToken = this.getToken();
     console.log('🔍 Token check:', {
       hasRefreshToken: !!currentRefreshToken,
       hasAccessToken: !!currentAccessToken,
       refreshTokenLength: currentRefreshToken?.length || 0,
       accessTokenLength: currentAccessToken?.length || 0
     });

     if (!currentRefreshToken) {
       console.log('JwtService interceptor: No refresh token available, logging out immediately');

       // Clear auth data
       localStorage.removeItem(this.jwtConfig.storageTokenKeyName);
       localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName);
       localStorage.removeItem('userData');
       localStorage.removeItem('fetchUser');
       localStorage.removeItem('reactQueryDevtoolsSortFn');
       localStorage.setItem('showInstallModalStep', 3);

       // Clear all cookies
       document.cookie.split(";").forEach((cookie) => {
         const name = cookie.split("=")[0].trim();
         document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
       });

       // Show toast notification
       import('../../../utils/toastManager').then(({ default: toastManager }) => {
         toastManager.showError({
           message: 'Your session has expired. Please login again.',
           status: 401
         });
       });

       // Redirect to login
       console.log('🚪 No token - Redirecting to login page');
       setTimeout(() => {
         window.location.href = '/login';
       }, 100);

       return Promise.reject(error);
     }

     if (!this.isAlreadyFetchingAccessToken) {
     this.isAlreadyFetchingAccessToken = true
     console.log('JwtService interceptor: Starting token refresh process');

     // Use proper promise handling for the refresh token request
     this.refreshToken()
     .then(r => {
     this.isAlreadyFetchingAccessToken = false
     console.log('JwtService interceptor: Token refresh successful');
     console.log('🔍 Refresh response:', r.data);

     // ✅ CRITICAL FIX: Backend returns {token, token_type} NOT {accessToken, refreshToken}
     // The Laravel backend's refreshToken method returns:
     // { token: "new_token", token_type: "Bearer" }
     // We need to map this to our expected format
     const newAccessToken = r.data.token || r.data.accessToken;
     const newRefreshToken = r.data.refreshToken || newAccessToken; // Use same token as refresh token

     if (!newAccessToken) {
       console.error('❌ No token in refresh response!', r.data);
       throw new Error('No token in refresh response');
     }

     // ** Update accessToken in localStorage
     this.setToken(newAccessToken)
     this.setRefreshToken(newRefreshToken)

     // ✅ FIX #5: Update userData object in localStorage with new tokens
     try {
       const userData = JSON.parse(localStorage.getItem('userData') || '{}');
       if (userData && Object.keys(userData).length > 0) {
         userData.accessToken = newAccessToken;
         userData.refreshToken = newRefreshToken;
         localStorage.setItem('userData', JSON.stringify(userData));
         console.log('JwtService: Updated userData with new tokens');
       }
     } catch (error) {
       console.error('JwtService: Failed to update userData:', error);
     }

     // ✅ FIX: Use newAccessToken variable instead of r.data.accessToken
     // This ensures we use the correctly extracted token regardless of response format
     this.onAccessTokenFetched(newAccessToken)
     })
     .catch(refreshError => {
     // Handle refresh token failure
     this.isAlreadyFetchingAccessToken = false;
     console.error('JwtService interceptor: Token refresh failed:', refreshError);

     // Error is already marked as handled above (line 75)
     // No need to redirect here as refreshToken method already handles it
     });
     }

     // Only retry the request if we haven't marked refresh token as failed
     if (!this.refreshTokenFailed && !this.isLoggingOut) {
     console.log('JwtService interceptor: Setting up retry for original request');
     const retryOriginalRequest = new Promise((resolve, reject) => {
     this.addSubscriber(accessToken => {
     try {
     console.log('JwtService interceptor: Retrying original request with new token');
     // ** Make sure to assign accessToken according to your response.
     // ** Check: https://pixinvent.ticksy.com/ticket/2413870
     // ** Change Authorization header
     originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
     resolve(this.axiosIns(originalRequest))
     } catch (err) {
     console.error('JwtService interceptor: Error retrying request:', err);
     reject(err)
     }
     })
     });
     return retryOriginalRequest;
     } else {
     console.log('JwtService interceptor: Not retrying request because refresh token has failed or logout in progress');
     // Error is already marked as handled above (line 75)
     }
     }
     return Promise.reject(error)
     }
    )
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter(callback => callback(accessToken))

    // Dispatch custom event for WebSocket to listen and reconnect with new token
    window.dispatchEvent(new CustomEvent('tokenRefreshed', {
      detail: { accessToken }
    }));
    console.log('🔐 Token refreshed - dispatched tokenRefreshed event');
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)

    // Analyze the token being stored
    if (value && process.env.NODE_ENV === 'development') {
      try {
        const tokenParts = value.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const now = Math.floor(Date.now() / 1000);
          const expiresAt = payload.exp;
          const timeUntilExpiry = expiresAt - now;

          console.log('🔐 setToken called:', {
            tokenPreview: value.substring(0, 50) + '...',
            expiresAt: new Date(expiresAt * 1000).toISOString(),
            timeUntilExpiry: `${Math.floor(timeUntilExpiry / 60)} minutes`,
            isAlreadyExpired: timeUntilExpiry <= 0
          });

          if (timeUntilExpiry <= 0) {
            console.error('❌ WARNING: Storing an ALREADY EXPIRED token!');
          }
        }
      } catch (e) {
        console.error('Failed to analyze token in setToken:', e);
      }
    }

    // Dispatch event to notify WebSocket of token update
    window.dispatchEvent(new CustomEvent('authTokenUpdated', {
      detail: { accessToken: value }
    }));
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)

    if (process.env.NODE_ENV === 'development') {
      console.log('🔐 setRefreshToken called:', {
        tokenPreview: value ? value.substring(0, 50) + '...' : 'NULL'
      });
    }
  }

  login(lang, ...args) {
    // Define the headers object to include the 'x-lang' header
    const headers = {
      'x-lang': `${lang}` // Set the x-lang header with the value 'en'
    }

    // Use Axios to make the POST request with the provided headers
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args, { headers })
  }

  register(lang, ...args) {
    const headers = {
      'x-lang': `${lang}` // Set the x-lang header with the value 'en'
    }

    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args, { headers })
  }

  refreshToken() {
   // If refresh token has already failed, don't try again
   if (this.refreshTokenFailed) {
   console.log('Refresh token has already failed, not attempting again');
   return Promise.reject(new Error('Refresh token has already failed'));
   }

   // If logout is in progress, don't try to refresh
   if (this.isLoggingOut) {
   console.log('Logout in progress, not attempting token refresh');
   return Promise.reject(new Error('Logout in progress'));
   }

   // ✅ FIX #7: Check cooldown to prevent excessive refresh attempts
   const now = Date.now();
   if (now - this.lastRefreshTime < this.refreshCooldown) {
     const remainingCooldown = Math.ceil((this.refreshCooldown - (now - this.lastRefreshTime)) / 1000);
     console.log(`Token refresh on cooldown (${remainingCooldown}s remaining), returning current token`);

     // Return current token wrapped in expected format
     const currentToken = this.getToken();
     return Promise.resolve({
       data: {
         accessToken: currentToken,
         refreshToken: currentToken
       }
     });
   }

   // Update last refresh time
   this.lastRefreshTime = now;

   const currentToken = this.getToken();
   const currentRefreshToken = this.getRefreshToken();

   console.log('JwtService: Attempting to refresh token');
   console.log('🔍 Token details:', {
     hasAccessToken: !!currentToken,
     hasRefreshToken: !!currentRefreshToken,
     accessTokenLength: currentToken?.length || 0,
     refreshTokenLength: currentRefreshToken?.length || 0,
     tokensMatch: currentToken === currentRefreshToken,
     accessTokenPreview: currentToken ? currentToken.substring(0, 50) + '...' : 'MISSING',
     refreshTokenPreview: currentRefreshToken ? currentRefreshToken.substring(0, 50) + '...' : 'MISSING'
   });

   // Decode and log JWT token details to check expiration
   if (currentToken) {
     try {
       const tokenParts = currentToken.split('.');
       if (tokenParts.length === 3) {
         const payload = JSON.parse(atob(tokenParts[1]));
         const now = Math.floor(Date.now() / 1000);
         const issuedAt = payload.iat;
         const expiresAt = payload.exp;
         const timeUntilExpiry = expiresAt - now;

         console.log('🔍 JWT Token Analysis:', {
           issuedAt: new Date(issuedAt * 1000).toISOString(),
           expiresAt: new Date(expiresAt * 1000).toISOString(),
           currentTime: new Date(now * 1000).toISOString(),
           tokenAge: `${Math.floor((now - issuedAt) / 60)} minutes`,
           timeUntilExpiry: `${Math.floor(timeUntilExpiry / 60)} minutes`,
           isExpired: timeUntilExpiry <= 0,
           payload: payload
         });

         if (timeUntilExpiry <= 0) {
           console.error('❌ Token is already expired! Backend gave us an expired token.');
         }
       }
     } catch (e) {
       console.error('Failed to decode JWT token:', e);
     }
   }

   // ✅ UPDATED: Based on backend API docs, the refresh endpoint expects:
   // - Empty body: {}
   // - Current token in Authorization header (already added by request interceptor)
   // - Response: {status: 200, data: {token: "new_token", token_type: "Bearer"}}

   console.log('🔍 Refresh request details:', {
     endpoint: this.jwtConfig.refreshEndpoint,
     method: 'POST',
     body: {},
     willUseToken: currentToken ? currentToken.substring(0, 50) + '...' : 'NONE'
   });

   return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {})
   .then(response => {
   console.log('JwtService: Token refresh successful', response.data);

   // ✅ Handle backend API response structure
   // Backend returns: {status: 200, data: {token: "...", token_type: "Bearer"}}
   if (response.data && response.data.data && response.data.data.token) {
     // Transform to expected format for compatibility
     return {
       data: {
         accessToken: response.data.data.token,
         refreshToken: response.data.data.token, // Same token used for both
         token_type: response.data.data.token_type
       }
     };
   }

   // Fallback for other response formats
   return response;
   })
   .catch(error => {
   console.error('JwtService: Refresh token request failed:', error);
   console.log('🔍 Refresh error details:', {
     status: error.response?.status,
     statusText: error.response?.statusText,
     data: error.response?.data,
     message: error.message,
     requestConfig: {
       url: error.config?.url,
       method: error.config?.method,
       baseURL: error.config?.baseURL,
       headers: error.config?.headers,
       data: error.config?.data
     }
   });

   console.log('🔍 Full error response:', JSON.stringify({
     status: error.response?.status,
     statusText: error.response?.statusText,
     headers: error.response?.headers,
     data: error.response?.data
   }, null, 2));

   // Set flags to prevent further requests
   this.refreshTokenFailed = true;
   this.isLoggingOut = true;

   // Clear auth data FIRST before showing any UI
   console.log('🧹 Clearing authentication data...');
   localStorage.removeItem(this.jwtConfig.storageTokenKeyName);
   localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName);
   localStorage.removeItem('userData');
   localStorage.removeItem('fetchUser');
   localStorage.removeItem('reactQueryDevtoolsSortFn');
   localStorage.setItem('showInstallModalStep', 3);

   // Clear all cookies
   document.cookie.split(";").forEach((cookie) => {
     const name = cookie.split("=")[0].trim();
     document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
   });

   // Log additional information about the error
   if (error.response) {
     console.error('JwtService: Error response data:', error.response.data);
     console.error('JwtService: Error response status:', error.response.status);
   }

   // Show toast notification (async, but don't wait for it)
   import('../../../utils/toastManager').then(({ default: toastManager }) => {
     toastManager.showError({
       message: 'Your session has expired. Please login again.',
       status: 401
     });
   }).catch(err => {
     console.error('Failed to load toastManager:', err);
   });

   // Redirect to login page immediately (use setTimeout to ensure it happens after current execution)
   console.log('🚪 Session expired - Redirecting to login page in 100ms');
   setTimeout(() => {
     console.log('🚪 Executing redirect now...');
     window.location.href = '/login';
   }, 100);

   return Promise.reject(error);
   });
  }
}
